export const initSubmenuScroll = () => {
    // Define button selectors as constants
    const BTN_LEVEL_4 = '.btn-submenu--level-4';
    const BTN_LEVEL_5 = '.btn-submenu--level-5';
    const BTN_BACK = '.submenu__button-back';

    // Breakpoint for mobile (matches the 992px check elsewhere)
    const MOBILE_BREAKPOINT = 992;

    // Default width in case measurement fails - increased slightly
    let firstLevelWidth = 390; // Increased from 377.25
    const additionalShift = 12;

    // Store the current slide level (0 = base, 1 = level 4, 2 = level 5)
    let currentSlideLevel = 0;

    // Helper function to check if we're on mobile
    const isMobile = () => {
        return window.innerWidth <= MOBILE_BREAKPOINT;
    };

    // Improved function to measure menu width - more reliable
    const measureFirstLevelWidth = () => {
        // Only measure if viewport is wider than mobile breakpoint
        if (isMobile()) {
            return;
        }

        // Find visible level-1 items - using a more robust selector
        const level1Items = document.querySelectorAll('.submenu-item--level-1');

        // Try multiple items and take the largest width
        let maxWidth = 0;
        level1Items.forEach(item => {
            const rect = item.getBoundingClientRect();
            if (rect.width > maxWidth) {
                maxWidth = rect.width;
            }
        });

        // Only update if we found a valid width
        if (maxWidth > 0) {
            firstLevelWidth = maxWidth;
        }
    };

    // Measure width before each transition
    const measureBeforeTransition = () => {
        // Measure the width
        measureFirstLevelWidth();
        return firstLevelWidth + additionalShift;
    };

    // Handle level 4 button click - slides to level 4
    const handleLevel4Click = (event) => {
        // Skip on mobile
        if (isMobile()) return;

        const button = event.target.closest(BTN_LEVEL_4);
        if (!button) return;

        // Get the root submenu container
        const rootSubmenu = button.closest('.submenu__overflow')?.querySelector('.submenu');

        if (rootSubmenu) {
            // Force a measurement right before the transition
            const shiftAmount = measureBeforeTransition();
            rootSubmenu.style.transform = `translateX(-${shiftAmount}px)`;
            currentSlideLevel = 1;

            // Show the back button at the wrapper level
            const wrapper = rootSubmenu.closest('.nav-overlay__wrapper');
            if (wrapper) {
                const backButton = wrapper.querySelector(BTN_BACK);
                if (backButton) {
                    backButton.style.display = 'inline-flex';
                }
            }
        }
    };

    // Handle level 5 button click - slides to level 5
    const handleLevel5Click = (event) => {
        // Skip on mobile
        if (isMobile()) return;

        const button = event.target.closest(BTN_LEVEL_5);
        if (!button) return;

        // Get the root submenu container
        const rootSubmenu = button.closest('.submenu__overflow')?.querySelector('.submenu');

        if (rootSubmenu) {
            // Force a measurement right before the transition
            const baseShift = measureBeforeTransition();
            const shiftAmount = baseShift * 2;
            rootSubmenu.style.transform = `translateX(-${shiftAmount}px)`;
            currentSlideLevel = 2;

            // Show the back button at the wrapper level
            const wrapper = rootSubmenu.closest('.nav-overlay__wrapper');
            if (wrapper) {
                const backButton = wrapper.querySelector(BTN_BACK);
                if (backButton) {
                    backButton.style.display = 'inline-flex';
                }
            }
        }
    };

    // Back button handler - handles sliding back
    const handleBackButton = (event) => {
        // Skip on mobile
        if (isMobile()) return;

        const button = event.target.closest(BTN_BACK);
        if (!button) return;

        // Find the root submenu
        const rootSubmenu = button.closest('.nav-overlay__wrapper')?.querySelector('.submenu');

        if (rootSubmenu) {
            if (currentSlideLevel === 2) {
                // If at level 5, go back to level 4
                // Force a measurement right before the transition
                const shiftAmount = measureBeforeTransition();
                rootSubmenu.style.transform = `translateX(-${shiftAmount}px)`;
                currentSlideLevel = 1;

                // Remove 'btn-submenu--active' class from all level-5 buttons
                const activeButtons5 = document.querySelectorAll(`${BTN_LEVEL_5}.btn-submenu--active`);
                activeButtons5.forEach(activeButton => {
                    activeButton.classList.remove('btn-submenu--active');
                });

                // Remove 'submenu-link--active' class ONLY from level-5 links
                const activeLinks5 = document.querySelectorAll('.submenu-link--level-5.submenu-link--active');
                activeLinks5.forEach(activeLink => {
                    activeLink.classList.remove('submenu-link--active');
                });
            } else if (currentSlideLevel === 1) {
                // If at level 4, go back to base
                rootSubmenu.style.transform = 'translateX(0)';
                currentSlideLevel = 0;

                // Hide the back button
                button.style.display = 'none';

                // Remove 'btn-submenu--active' class from all level-4 buttons
                const activeButtons4 = document.querySelectorAll(`${BTN_LEVEL_4}.btn-submenu--active`);
                activeButtons4.forEach(activeButton => {
                    activeButton.classList.remove('btn-submenu--active');
                });

                // Remove 'submenu-link--active' class ONLY from level-4 links
                const activeLinks4 = document.querySelectorAll('.submenu-link--level-4.submenu-link--active');
                activeLinks4.forEach(activeLink => {
                    activeLink.classList.remove('submenu-link--active');
                });
            }

            // Only remove the LAST item from all breadcrumb elements
            document.querySelectorAll('.menu-breadcrumb').forEach(breadcrumb => {
                const items = breadcrumb.querySelectorAll('li');
                // Only remove if there are at least 2 items (keep the root item)
                if (items.length > 1) {
                    // Remove only the last item
                    const lastItem = items[items.length - 1];
                    lastItem.remove();
                }
            });
        }
    };

    // Reset transform when menu is closed
    const handleMenuClose = () => {
        // Only perform on desktop
        if (isMobile()) return;

        document.querySelectorAll('.submenu__overflow .submenu').forEach(submenu => {
            submenu.style.transform = 'translateX(0)';
        });

        // Hide all back buttons
        document.querySelectorAll(BTN_BACK).forEach(button => {
            button.style.display = 'none';
        });

        // Reset the slide level
        currentSlideLevel = 0;
    };

    // Setup all event handlers
    const initialize = () => {
        // Try to measure initial widths with a slight delay to ensure DOM is ready
        setTimeout(measureFirstLevelWidth, 300);

        // Add a second measurement attempt with a longer delay
        // This helps with slow-loading pages
        setTimeout(measureFirstLevelWidth, 1000);

        // Add event listeners
        document.addEventListener('click', (e) => {
            handleLevel4Click(e);
            handleLevel5Click(e);
            handleBackButton(e);
        });

        // Listen for menu close events
        document.querySelectorAll('.btn-close-menu').forEach(button => {
            button.addEventListener('click', handleMenuClose);
        });

        // Also reset when clicking the first breadcrumb item
        document.querySelectorAll('.menu-breadcrumb').forEach(breadcrumb => {
            const firstItem = breadcrumb.querySelector('li:first-child');
            if (firstItem) {
                firstItem.addEventListener('click', handleMenuClose);
            }
        });

        // Add styles for transitions - making them slightly longer
        const style = document.createElement('style');
        style.textContent = `
        /* Only apply transitions on desktop */
        @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
            .submenu {
                transition: transform 0.35s ease;
            }
        }
        `;
        document.head.appendChild(style);

        // Update measurements on resize and also reset if switching between mobile/desktop
        window.addEventListener('resize', () => {
            // If switching to mobile, reset any active transformations
            if (isMobile()) {
                document.querySelectorAll('.submenu__overflow .submenu').forEach(submenu => {
                    submenu.style.transform = '';
                });

                document.querySelectorAll(BTN_BACK).forEach(button => {
                    button.style.display = 'none';
                });

                currentSlideLevel = 0;
            } else {
                // On desktop, measure the width
                measureFirstLevelWidth();
            }
        });

        // Add measurement trigger when any menu opens
        document.querySelectorAll('.btn-open-menu, .menu-toggle').forEach(button => {
            button.addEventListener('click', () => {
                // Only measure on desktop
                if (!isMobile()) {
                    // Wait a moment for menu to be visible before measuring
                    setTimeout(measureFirstLevelWidth, 100);
                }
            });
        });

        // Hide all back buttons initially
        document.querySelectorAll(BTN_BACK).forEach(button => {
            button.style.display = 'none';
        });
    };

    // Create a MutationObserver to watch for DOM changes
    // This helps with single-page applications or lazy-loaded menus
    const setupMutationObserver = () => {
        // Create an observer instance
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    // If new nodes were added, check if they contain our menu elements
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1 &&
                            (node.classList?.contains('submenu') ||
                                node.querySelector?.('.submenu'))) {
                            // Menu elements were added, re-measure (only on desktop)
                            if (!isMobile()) {
                                setTimeout(measureFirstLevelWidth, 100);
                            }
                        }
                    });
                }
            });
        });

        // Start observing the document with the configured parameters
        observer.observe(document.body, { childList: true, subtree: true });
    };

    // Start initialization
    initialize();
    setupMutationObserver();
};

// Initialize the module
initSubmenuScroll();
